import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Switch} from '@/web-components';
import {HorizontalFlexWrapper} from './app_salesforce_style';
import {SelectionElementLabel} from '../../../project-tab/projects/project-settings/ProjectSettings_styled';
import DirectApi from '../../../../directApi';
import {createToast} from '../../../shared/components/toasts/toast';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';

const LinkedFieldsWrapper = styled.div`
	margin: 8px 0;
`;

const SALESFORCE_SYNC_FIELDS = ['opportunityName', 'opportunityAmount', 'opportunityDates'];

const SALESFORCE_SYNC_FIELD_LABEL_IDS = {
	opportunityName: 'settings.app_catalog.salesforce.sync_direction.opportunity_name',
	opportunityAmount: 'settings.app_catalog.salesforce.sync_direction.opportunity_amount',
	opportunityDates: 'settings.app_catalog.salesforce.sync_direction.opportunity_dates',
};

const bidirectionalKey = field => `${field}Bidirectional`;

const readBidirectionalFromRaw = (raw, field) => {
	if (!raw || typeof raw !== 'object') {
		return true;
	}
	const key = bidirectionalKey(field);
	const top = raw[key];
	if (typeof top === 'boolean') {
		return top;
	}
	if (typeof top === 'string') {
		return top === 'true';
	}
	const entry = raw[field];
	if (entry && typeof entry === 'object' && typeof entry.bidirectional === 'boolean') {
		return entry.bidirectional;
	}
	return true;
};

const buildSyncPutBody = bidirectional => {
	const body = {};
	for (const field of SALESFORCE_SYNC_FIELDS) {
		body[bidirectionalKey(field)] = Boolean(bidirectional[field]);
	}
	return body;
};

const fetchPutJson = (endpoint, body) => {
	const headers = new Headers();
	headers.append('Content-Type', 'application/json');
	return fetch(DirectApi.graphqlServerEndpoint(endpoint), {
		headers,
		credentials: 'include',
		method: 'PUT',
		body: JSON.stringify(body),
	});
};

const readLinkedFromRaw = (linkedJson, field) => {
	const linkedFields = linkedJson?.linkedFields;
	if (!linkedFields || typeof linkedFields !== 'object') {
		return true;
	}
	const entry = linkedFields[field];
	if (entry == null) {
		return true;
	}
	if (typeof entry === 'object' && typeof entry.linked === 'boolean') {
		return entry.linked;
	}
	return Boolean(entry);
};

const SalesforceBidirectionalSyncSettings = ({externalLinkedFields}) => {
	const intl = useIntl();
	const [bidirectional, setBidirectional] = useState(null);
	const [linkedFields, setLinkedFields] = useState(null);
	const bidirectionalRef = useRef(bidirectional);
	bidirectionalRef.current = bidirectional;
	const linkedFieldsRef = useRef(linkedFields);
	linkedFieldsRef.current = linkedFields;
	const initStartedRef = useRef(false);

	useEffect(() => {
		if (externalLinkedFields) {
			setLinkedFields(externalLinkedFields);
		}
	}, [externalLinkedFields]);

	useEffect(() => {
		if (initStartedRef.current) {
			return undefined;
		}
		initStartedRef.current = true;
		let cancelled = false;

		Promise.all([
			DirectApi.Fetch_Raw('salesforce/sync_direction_settings')
				.then(res => (res.ok ? res.json() : {}))
				.catch(() => ({})),
			DirectApi.Fetch_Raw('salesforce/linked_fields')
				.then(res => (res.ok ? res.json() : {}))
				.catch(() => ({})),
		]).then(([syncJson, linkedJson]) => {
			if (cancelled) {
				return;
			}
			const linked = {};
			for (const field of SALESFORCE_SYNC_FIELDS) {
				linked[field] = readLinkedFromRaw(linkedJson, field);
			}
			setLinkedFields(linked);

			const nextBidirectional = {};
			for (const field of SALESFORCE_SYNC_FIELDS) {
				nextBidirectional[field] = readBidirectionalFromRaw(syncJson, field);
			}
			setBidirectional(nextBidirectional);
		});

		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (!bidirectional || !linkedFields) {
			return;
		}
		let changed = false;
		const next = {...bidirectional};
		for (const field of SALESFORCE_SYNC_FIELDS) {
			if (!linkedFields[field] && next[field]) {
				next[field] = false;
				changed = true;
			}
		}
		if (!changed) {
			return;
		}
		setBidirectional(next);
		fetchPutJson('salesforce/sync_direction_settings', buildSyncPutBody(next)).catch(() => {});
	}, [linkedFields, bidirectional]);

	const updateSyncDirection = useCallback(
		(field, previousChecked) => {
			if (!linkedFieldsRef.current?.[field]) {
				return undefined;
			}
			const nextBidirectional = {...bidirectionalRef.current, [field]: !previousChecked};
			setBidirectional(nextBidirectional);
			fetchPutJson('salesforce/sync_direction_settings', buildSyncPutBody(nextBidirectional))
				.then(() => {
					createToast({duration: 2000, message: intl.formatMessage({id: 'common.changes_saved'})});
				})
				.catch(() => {});
		},
		[intl]
	);

	if (!bidirectional) {
		return <InlineLoader />;
	}

	return (
		<>
			{SALESFORCE_SYNC_FIELDS.map(field => (
				<LinkedFieldsWrapper key={`sync-${field}`}>
					<HorizontalFlexWrapper>
						<Switch
							checked={bidirectional[field]}
							locked={!linkedFields?.[field]}
							onChange={previousChecked => updateSyncDirection(field, previousChecked)}
							cy={`salesforce-sync-direction-${field}`}
						/>
						<SelectionElementLabel>
							{intl.formatMessage({id: SALESFORCE_SYNC_FIELD_LABEL_IDS[field]})}
						</SelectionElementLabel>
					</HorizontalFlexWrapper>
				</LinkedFieldsWrapper>
			))}
		</>
	);
};

export default SalesforceBidirectionalSyncSettings;
